import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { DateTime } from 'luxon';
import { Control, useController, useForm } from 'react-hook-form';
import { Alert, Stack, Unstable_Grid2 as Grid } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';

import { AMCR_FINDER_NAME_PATTERN, AMCR_MAX_DEPTH_CM } from '../../consts';
import { useYupValidationResolver } from '../../helpers';
import { useVocabulary } from '../../i18n';
import {
  DateTimeField,
  FieldLabel,
  FormField,
  SurfaceAutocomplete,
  SurfaceTextField,
} from '../design-system';
import { AmcrFindSummary } from './AmcrFindSummary';
import { isCoordinate, toNumber } from './coordinates';
import { amcrFieldLabels, amcrPreviewMessages } from './messages';

export interface AmcrVocabularyEntry {
  id: string;
  label: string;
}

export interface AmcrVocabularyOption extends AmcrVocabularyEntry {
  /** First Acolyte key mapped to the entry, for labels and colours. */
  key: string;
  /** Acolyte find type category or parent era, as in the find form. */
  group: string | null;
}

export interface AmcrVocabularies {
  types: AmcrVocabularyOption[];
  materials: AmcrVocabularyOption[];
  eras: AmcrVocabularyOption[];
  contexts: AmcrVocabularyOption[];
}

export interface AmcrFindRecordPreview {
  projectId: string;
  type: AmcrVocabularyEntry;
  material: AmcrVocabularyEntry;
  era: AmcrVocabularyEntry;
  context: AmcrVocabularyEntry;
  accessibility: AmcrVocabularyEntry;
  depth: number;
  count: number;
  note: string | null;
  localization: string | null;
  foundOn: string | null;
  latitude: number | null;
  longitude: number | null;
  finder: { id: string; name: string };
}

/** Matches the `AmcrFindRecordInput` GraphQL input. */
export interface AmcrFindRecordEdits {
  projectId: string;
  typeId: string;
  materialId: string;
  eraId: string;
  contextId: string;
  depth: number | null;
  count: number | null;
  note: string;
  localization: string;
  foundOn: string | null;
  latitude: number | null;
  longitude: number | null;
  finderName: string;
}

export interface AmcrFindPreviewValues {
  projectId: string;
  type: AmcrVocabularyEntry;
  material: AmcrVocabularyEntry;
  era: AmcrVocabularyEntry;
  context: AmcrVocabularyEntry;
  depth: string;
  count: string;
  foundOn: DateTime | null;
  latitude: string;
  longitude: string;
  finderName: string;
  localization: string;
  note: string;
}

interface AmcrFindPreviewProps {
  record: AmcrFindRecordPreview;
  projectIds: string[];
  /** The user's own AMCR name, "anonym", and previously used names. */
  finderNames: string[];
  vocabularies: AmcrVocabularies;
  disabled: boolean;
  onSubmit: (edits: AmcrFindRecordEdits) => void;
}

type PreviewControl = Control<AmcrFindPreviewValues>;
type VocabularyFieldName = 'type' | 'material' | 'era' | 'context';
type TextFieldName =
  | 'depth'
  | 'count'
  | 'latitude'
  | 'longitude'
  | 'localization'
  | 'note';

export const AMCR_FIND_PREVIEW_FORM_ID = 'amcr-find-preview-form';

/** Placeholder id AMCR uses for a person it matches or creates by name. */
const NEW_PERSON_ID = ':tba';

const readOnlyInputProps = { readOnly: true };
const errorListStyles = { m: 0, pl: 2 };

const getEntryLabel = (entry: AmcrVocabularyEntry) => entry.label;
const getEntryKey = (entry: AmcrVocabularyEntry) => entry.id;
const isSameEntry = (option: AmcrVocabularyEntry, value: AmcrVocabularyEntry) =>
  option.id === value.id;

const isValidCoordinatePair = ({
  latitude,
  longitude,
}: AmcrFindPreviewValues) => {
  if (!latitude.trim() && !longitude.trim()) {
    return true;
  }

  return isCoordinate(latitude, 90) && isCoordinate(longitude, 180);
};

const toFormValues = (
  record: AmcrFindRecordPreview
): AmcrFindPreviewValues => ({
  projectId: record.projectId,
  type: record.type,
  material: record.material,
  era: record.era,
  context: record.context,
  depth: String(record.depth),
  count: String(record.count),
  foundOn: record.foundOn ? DateTime.fromISO(record.foundOn) : null,
  latitude: record.latitude?.toString() ?? '',
  longitude: record.longitude?.toString() ?? '',
  finderName: record.finder.name,
  localization: record.localization ?? '',
  note: record.note ?? '',
});

const toEdits = (values: AmcrFindPreviewValues): AmcrFindRecordEdits => ({
  projectId: values.projectId,
  typeId: values.type.id,
  materialId: values.material.id,
  eraId: values.era.id,
  contextId: values.context.id,
  depth: toNumber(values.depth),
  count: toNumber(values.count),
  note: values.note,
  localization: values.localization,
  foundOn: values.foundOn?.isValid ? values.foundOn.toISODate() : null,
  latitude: toNumber(values.latitude),
  longitude: toNumber(values.longitude),
  finderName: values.finderName.trim(),
});

interface PreviewTextFieldProps {
  control: PreviewControl;
  name: TextFieldName;
  label: React.ReactNode;
  disabled: boolean;
  type?: string;
  multiline?: boolean;
  helperText?: string;
}

const PreviewTextField = ({
  control,
  name,
  label,
  disabled,
  type = 'text',
  multiline,
  helperText,
}: PreviewTextFieldProps) => {
  const { field } = useController<AmcrFindPreviewValues, TextFieldName>({
    control,
    name,
  });
  const id = `amcr-preview-${name}`;

  return (
    <FormField clampLabel>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <SurfaceTextField
        fullWidth
        id={id}
        type={type}
        disabled={disabled}
        multiline={multiline}
        minRows={multiline ? 2 : undefined}
        helperText={helperText}
        surface="sunken"
        {...field}
      />
    </FormField>
  );
};

interface VocabularyFieldProps {
  control: PreviewControl;
  name: VocabularyFieldName;
  label: React.ReactNode;
  options: AmcrVocabularyOption[];
  disabled: boolean;
  groupBy?: (option: AmcrVocabularyOption) => string;
}

const VocabularyField = ({
  control,
  name,
  label,
  options,
  disabled,
  groupBy,
}: VocabularyFieldProps) => {
  const { field } = useController<AmcrFindPreviewValues, VocabularyFieldName>({
    control,
    name,
  });
  const id = `amcr-preview-${name}`;

  const handleChange = (_event: SyntheticEvent, value: AmcrVocabularyEntry) => {
    field.onChange(value);
  };

  const textFieldProps = { id, inputRef: field.ref, onBlur: field.onBlur };

  return (
    <FormField clampLabel>
      <FieldLabel htmlFor={id}>{label}</FieldLabel>
      <SurfaceAutocomplete<AmcrVocabularyEntry, false, true, false>
        disableClearable
        options={options}
        value={field.value}
        disabled={disabled}
        groupBy={groupBy}
        getOptionLabel={getEntryLabel}
        getOptionKey={getEntryKey}
        isOptionEqualToValue={isSameEntry}
        onChange={handleChange}
        surface="sunken"
        textFieldProps={textFieldProps}
      />
    </FormField>
  );
};

interface FoundOnFieldProps {
  control: PreviewControl;
  disabled: boolean;
}

const FoundOnField = ({ control, disabled }: FoundOnFieldProps) => {
  const { field } = useController<AmcrFindPreviewValues, 'foundOn'>({
    control,
    name: 'foundOn',
  });

  return (
    <DateTimeField
      clampLabel
      enableTime={false}
      id="amcr-preview-foundOn"
      label={<FormattedMessage {...amcrFieldLabels.foundOn} />}
      disabled={disabled}
      inputRef={field.ref}
      name={field.name}
      onBlur={field.onBlur}
      onChange={field.onChange}
      value={field.value}
    />
  );
};

interface FinderFieldProps {
  control: PreviewControl;
  options: string[];
  disabled: boolean;
  helperText?: string;
}

/** Offers known finders but accepts any new "Surname, Given name". */
const FinderField = ({
  control,
  options,
  disabled,
  helperText,
}: FinderFieldProps) => {
  const { field } = useController<AmcrFindPreviewValues, 'finderName'>({
    control,
    name: 'finderName',
  });

  const handleChange = (_event: SyntheticEvent, value: string | null) => {
    field.onChange(value ?? '');
  };

  const handleInputChange = (_event: SyntheticEvent, value: string) => {
    field.onChange(value);
  };

  const textFieldProps = {
    id: 'amcr-preview-finderName',
    inputRef: field.ref,
    onBlur: field.onBlur,
    helperText,
  };

  return (
    <FormField clampLabel>
      <FieldLabel htmlFor="amcr-preview-finderName">
        <FormattedMessage {...amcrFieldLabels.finder} />
      </FieldLabel>
      <SurfaceAutocomplete<string, false, true, true>
        freeSolo
        forcePopupIcon
        disableClearable
        options={options}
        value={field.value}
        inputValue={field.value}
        disabled={disabled}
        onChange={handleChange}
        onInputChange={handleInputChange}
        surface="sunken"
        textFieldProps={textFieldProps}
      />
    </FormField>
  );
};

interface ProjectFieldProps {
  control: PreviewControl;
  projectIds: string[];
  disabled: boolean;
}

const ProjectField = ({ control, projectIds, disabled }: ProjectFieldProps) => {
  const { field } = useController<AmcrFindPreviewValues, 'projectId'>({
    control,
    name: 'projectId',
  });

  const handleChange = (_event: SyntheticEvent, value: string) => {
    field.onChange(value);
  };

  const textFieldProps = {
    id: 'amcr-preview-projectId',
    inputRef: field.ref,
    onBlur: field.onBlur,
  };

  return (
    <FormField clampLabel>
      <FieldLabel htmlFor="amcr-preview-projectId">
        <FormattedMessage {...amcrFieldLabels.project} />
      </FieldLabel>
      <SurfaceAutocomplete<string, false, true, false>
        disableClearable
        options={projectIds}
        value={field.value}
        disabled={disabled}
        onChange={handleChange}
        surface="sunken"
        textFieldProps={textFieldProps}
      />
    </FormField>
  );
};

/**
 * Editable AMCR-PAS record. Values are AMCR vocabulary labels and identifiers
 * and must not be translated; edits change only the exported record.
 */
const AmcrFindPreview = React.memo(
  ({
    record,
    projectIds,
    finderNames,
    vocabularies,
    disabled,
    onSubmit,
  }: AmcrFindPreviewProps) => {
    const intl = useIntl();
    const vocabulary = useVocabulary();
    const [errors, setErrors] = useState<Array<{ error: string }>>([]);
    const groupTypeOption = useCallback(
      (option: AmcrVocabularyOption) => vocabulary('findType', option.group),
      [vocabulary]
    );
    const groupEraOption = useCallback(
      (option: AmcrVocabularyOption) => vocabulary('era', option.group),
      [vocabulary]
    );
    const finderOptions = useMemo(
      () => [...new Set([record.finder.name, ...finderNames])],
      [record.finder.name, finderNames]
    );

    const validationSchema = useMemo(
      () =>
        yup.object({
          depth: yup
            .string()
            .matches(/^\d+$/, intl.formatMessage(amcrPreviewMessages.depth))
            .test(
              'max-depth',
              intl.formatMessage(amcrPreviewMessages.depth),
              (value) => Number(value) <= AMCR_MAX_DEPTH_CM
            ),
          count: yup
            .string()
            .matches(
              /^[1-9]\d*$/,
              intl.formatMessage(amcrPreviewMessages.count)
            ),
          latitude: yup
            .string()
            .test(
              'coordinates',
              intl.formatMessage(amcrPreviewMessages.coordinates),
              (_value, context) => isValidCoordinatePair(context.parent)
            ),
          finderName: yup
            .string()
            .trim()
            .test(
              'finder',
              intl.formatMessage(amcrPreviewMessages.finderName),
              (value) =>
                finderOptions.includes(value) ||
                AMCR_FINDER_NAME_PATTERN.test(value)
            ),
        }),
      [intl, finderOptions]
    );
    const resolver = useYupValidationResolver(validationSchema, setErrors);
    const defaultValues = useMemo(() => toFormValues(record), [record]);
    const { control, handleSubmit, watch } = useForm<AmcrFindPreviewValues>({
      defaultValues,
      resolver,
    });

    const finderName = watch('finderName');
    const finderHelperText =
      finderName === record.finder.name && record.finder.id !== NEW_PERSON_ID
        ? record.finder.id
        : undefined;

    const handleValidSubmit = (values: AmcrFindPreviewValues) => {
      setErrors([]);
      onSubmit(toEdits(values));
    };

    return (
      <Stack
        component="form"
        id={AMCR_FIND_PREVIEW_FORM_ID}
        noValidate
        gap={2}
        onSubmit={handleSubmit(handleValidSubmit)}
      >
        <AmcrFindSummary control={control} vocabularies={vocabularies} />
        <Grid container spacing={2}>
          <Grid xs={12} sm={6}>
            <ProjectField
              control={control}
              projectIds={projectIds}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <FinderField
              control={control}
              options={finderOptions}
              disabled={disabled}
              helperText={finderHelperText}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <VocabularyField
              control={control}
              name="type"
              options={vocabularies.types}
              groupBy={groupTypeOption}
              label={<FormattedMessage {...amcrFieldLabels.type} />}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <VocabularyField
              control={control}
              name="material"
              options={vocabularies.materials}
              label={<FormattedMessage {...amcrFieldLabels.material} />}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <VocabularyField
              control={control}
              name="era"
              options={vocabularies.eras}
              groupBy={groupEraOption}
              label={<FormattedMessage {...amcrFieldLabels.era} />}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <VocabularyField
              control={control}
              name="context"
              options={vocabularies.contexts}
              label={<FormattedMessage {...amcrFieldLabels.context} />}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={6} sm={3}>
            <PreviewTextField
              control={control}
              name="depth"
              type="number"
              label={<FormattedMessage {...amcrFieldLabels.depth} />}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={6} sm={3}>
            <PreviewTextField
              control={control}
              name="count"
              type="number"
              label={<FormattedMessage {...amcrFieldLabels.count} />}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <FoundOnField control={control} disabled={disabled} />
          </Grid>
          <Grid xs={6} sm={3}>
            <PreviewTextField
              control={control}
              name="latitude"
              label={<FormattedMessage {...amcrFieldLabels.latitude} />}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={6} sm={3}>
            <PreviewTextField
              control={control}
              name="longitude"
              label={<FormattedMessage {...amcrFieldLabels.longitude} />}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12} sm={6}>
            <FormField clampLabel>
              <FieldLabel htmlFor="amcr-preview-accessibility">
                <FormattedMessage {...amcrFieldLabels.accessibility} />
              </FieldLabel>
              <SurfaceTextField
                fullWidth
                id="amcr-preview-accessibility"
                value={record.accessibility.label}
                surface="sunken"
                InputProps={readOnlyInputProps}
              />
            </FormField>
          </Grid>
          <Grid xs={12}>
            <PreviewTextField
              control={control}
              name="localization"
              label={<FormattedMessage {...amcrFieldLabels.localization} />}
              disabled={disabled}
            />
          </Grid>
          <Grid xs={12}>
            <PreviewTextField
              multiline
              control={control}
              name="note"
              label={<FormattedMessage {...amcrFieldLabels.note} />}
              disabled={disabled}
            />
          </Grid>
        </Grid>
        {errors.length > 0 && (
          <Alert severity="error" variant="outlined">
            <Stack component="ul" sx={errorListStyles}>
              {errors.map(({ error }) => (
                <li key={error}>{error}</li>
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    );
  }
);

AmcrFindPreview.displayName = 'AmcrFindPreview';

export { AmcrFindPreview };
